import { NEWSLETTER_MESSAGES } from "@config/newsletter";
import fetchBHApi from "@helper/fetchBHApi";
import {
  type PostConsentV3Item,
  type RespPostConsentV3,
  tryCatch,
} from "@sphtech/sph-web-ui";
import type { NewsletterPostResponse } from "@typings/Newsletter";

/**
 * Result of a newsletter submission. Each field, when defined, is the message
 * to write into the corresponding alert slot in the form. `undefined` means
 * "leave that slot unchanged".
 */
export type NewsletterSubmitResult = {
  errorMsg?: string;
  successMsg?: string;
};

/**
 * Submit the newsletter signup via the Consent v3 API
 * (`/_plat/api/v1/consent/sync`).
 */
export async function submitViaConsentV3(
  email: string,
  isMarketingConsent: boolean,
): Promise<NewsletterSubmitResult> {
  const endpoint = "/_plat/api/v1/consent/sync";
  const payload: { identifier: string; consents: PostConsentV3Item[] } = {
    identifier: email,
    consents: [
      { name: "bh_newsletter", status: "confirmed" },
      {
        name: "sph_marketing_email",
        status: isMarketingConsent ? "confirmed" : "withdrawn",
      },
    ],
  };
  const config: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
  const request = fetch(endpoint, config);
  const { error, response } = await tryCatch(request);

  if (error) {
    return { errorMsg: NEWSLETTER_MESSAGES.FAILED };
  }

  // Right now we are only cast, we dont really check if json has the correct shape.
  // Anyway the endpoint is internal, so it is mostly expected to return this type.
  // But it would still be recommended to check shape of the returned value instead.
  const json = (await response.json()) as RespPostConsentV3;

  if (!json.success) {
    return { errorMsg: NEWSLETTER_MESSAGES.FAILED };
  }

  return { successMsg: NEWSLETTER_MESSAGES.SUCCESS };
}

/**
 * Submit the newsletter signup via the legacy `newsletterSignup` endpoint.
 * Used while the Consent v3 feature flag is disabled.
 */
export async function submitViaLegacyEndpoint(
  email: string,
  isMarketingConsent: boolean,
): Promise<NewsletterSubmitResult> {
  try {
    const queryParams = new URLSearchParams({
      email,
      isMarketingChecked: String(isMarketingConsent),
    }).toString();
    const res = await fetchBHApi(`newsletterSignup?${queryParams}`, "GET");
    const data = res as NewsletterPostResponse;
    if (data.msg) {
      return { errorMsg: "", successMsg: data.msg };
    }
    if (data.errorMsg) {
      return { errorMsg: data.errorMsg };
    }
    return {};
  } catch {
    return { errorMsg: NEWSLETTER_MESSAGES.FAILED };
  }
}
